import {
  applicantSection,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * H2. Pomoc finansowa dla rodzin zastępczych i prowadzących rodzinne domy dziecka — art. 80–83 i 87 ustawy o wspieraniu
 * rodziny i systemie pieczy zastępczej (tekst z api.sejm.gov.pl/eli/acts/DU/2026/980 pobrany 2026-10-22). Brak wzoru
 * ogólnopolskiego; układ jak wzór PCPR Gliwice (świadczenie na utrzymanie: rodzaj rodziny, dziecko, data od której
 * przysługuje) — https://pcpr-gliwice.pl/wp-content/uploads/2024/11/wniosek-o-przyznanie-swiadczenia-na-pokrycie-kosztow-utrzymania-wychowanka-w-pieczy-zastepczej.pdf
 * (pobrany 2026-10-22). Rodzaj świadczenia (art. 80 / 81 / 83) jest wariantem wniosku i steruje załącznikami; wniosek
 * składa się osobno na każde dziecko. Dochód dziecka nie jest pytaniem — art. 80 ust. 2–5 (obniżanie świadczenia o dochód
 * dziecka) są uchylone. Akt urodzenia i numer rachunku to praktyka urzędów — nieobowiązkowe.
 */
const MAINTENANCE: EbiCondition = { field: "swiadczenie", equals: "utrzymanie" };
const ALLOWANCE: EbiCondition = { field: "swiadczenie", equals: "dodatek" };
const OPTIONAL: EbiCondition = { field: "swiadczenie", equals: "fakultatywne" };
const MONTHLY: EbiCondition = { any: [MAINTENANCE, ALLOWANCE] };
const ADULT_PUPIL: EbiCondition = { all: [MONTHLY, { field: "pelnoletni", equals: true }] };
/** Dane dziecka są wymagane przy świadczeniach na dziecko; przy utrzymaniu lokalu i remoncie — nieobowiązkowe. */
const CHILD_REQUIRED: EbiCondition = {
  any: [
    MONTHLY,
    {
      all: [
        OPTIONAL,
        {
          any: [
            { field: "fakultatywne", includes: "wypoczynek" },
            { field: "fakultatywne", includes: "potrzeby" },
          ],
        },
      ],
    },
  ],
};

const H2: IEbiFormDefinition = {
  code: "H2",
  version: 1,
  title: "Wniosek o pomoc finansową dla rodziny zastępczej lub rodzinnego domu dziecka",
  category: EEbiFormCategory.Disability,
  office: EEbiFormOffice.CountyUnit,
  description:
    "Świadczenie na pokrycie kosztów utrzymania dziecka, dodatek na dziecko z niepełnosprawnością albo świadczenia fakultatywne dla rodziny zastępczej i rodzinnego domu dziecka.",
  keywords: [
    "rodzina zastępcza",
    "rodzinny dom dziecka",
    "piecza zastępcza",
    "świadczenie na utrzymanie dziecka",
    "dodatek",
    "wypoczynek",
    "PCPR",
  ],
  organ: "Starosta (Prezydent Miasta) — Powiatowe Centrum Pomocy Rodzinie jako organizator rodzinnej pieczy zastępczej, właściwe według miejsca zamieszkania rodziny zastępczej",
  legalBasis: "art. 80–83 i art. 87 ustawy z dnia 9 czerwca 2011 r. o wspieraniu rodziny i systemie pieczy zastępczej",
  request: "Wnoszę o przyznanie świadczenia wskazanego we wniosku na dziecko umieszczone w rodzinie zastępczej lub rodzinnym domu dziecka.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty (sprawy świadczeń socjalnych); pełnomocnictwo — 17 zł.",
  deadlineLabel: "do miesiąca",
  deadline: "Do miesiąca, w sprawach szczególnie skomplikowanych do dwóch miesięcy (art. 35 KPA).",
  variantField: "swiadczenie",
  variants: {
    utrzymanie: { title: "Wniosek o świadczenie na pokrycie kosztów utrzymania dziecka w pieczy zastępczej" },
    dodatek: { title: "Wniosek o dodatek na pokrycie zwiększonych kosztów utrzymania dziecka" },
    fakultatywne: { title: "Wniosek o świadczenie fakultatywne dla rodziny zastępczej" },
  },
  sections: [
    {
      code: "rodzajSwiadczenia",
      title: "Rodzaj świadczenia",
      fields: [
        {
          code: "swiadczenie",
          type: "radio",
          label: "Wnoszę o przyznanie",
          required: true,
          options: [
            { value: "utrzymanie", label: "świadczenia na pokrycie kosztów utrzymania dziecka (art. 80)" },
            { value: "dodatek", label: "dodatku na pokrycie zwiększonych kosztów utrzymania dziecka (art. 81)" },
            { value: "fakultatywne", label: "świadczenia fakultatywnego (art. 83)" },
          ],
        },
        {
          code: "podstawaDodatku",
          type: "radio",
          label: "Dodatek przysługuje, ponieważ",
          required: true,
          visibleWhen: ALLOWANCE,
          options: [
            {
              value: "niepelnosprawnosc",
              label: "dziecko legitymuje się orzeczeniem o niepełnosprawności albo o znacznym lub umiarkowanym stopniu niepełnosprawności (art. 81 ust. 1)",
            },
            {
              value: "nieletni",
              label: "dziecko zostało umieszczone w rodzinie zastępczej zawodowej na podstawie ustawy o wspieraniu i resocjalizacji nieletnich (art. 81 ust. 2)",
            },
          ],
        },
        {
          code: "fakultatywne",
          type: "checkboxes",
          label: "Świadczenie fakultatywne",
          required: true,
          visibleWhen: OPTIONAL,
          options: [
            { value: "wypoczynek", label: "dofinansowanie do wypoczynku dziecka poza miejscem zamieszkania (art. 83 ust. 1 pkt 1)" },
            { value: "potrzeby", label: "pokrycie niezbędnych kosztów związanych z potrzebami przyjmowanego dziecka — jednorazowo (art. 83 ust. 1 pkt 2 lit. a)" },
            {
              value: "zdarzenie",
              label: "pokrycie kosztów związanych z wystąpieniem zdarzeń losowych lub innych zdarzeń mających wpływ na jakość sprawowanej opieki (art. 83 ust. 1 pkt 2 lit. b)",
            },
            { value: "lokal", label: "środki na utrzymanie lokalu mieszkalnego — rodzina zastępcza niezawodowa i zawodowa (art. 83 ust. 2)" },
            { value: "remont", label: "pokrycie kosztów niezbędnego remontu lokalu mieszkalnego — rodzina zastępcza zawodowa, raz w roku (art. 83 ust. 4)" },
          ],
        },
        feeInfo("infoKazdeDziecko", "Wniosek o świadczenie na dziecko składa się osobno dla każdego dziecka."),
      ],
    },
    applicantSection(),
    representationSection(),
    {
      code: "rodzina",
      title: "Rodzina zastępcza",
      fields: [
        {
          code: "rodzajRodziny",
          type: "radio",
          label: "Dziecko jest umieszczone",
          required: true,
          options: [
            { value: "spokrewniona", label: "w rodzinie zastępczej spokrewnionej" },
            { value: "niezawodowa", label: "w rodzinie zastępczej niezawodowej" },
            { value: "zawodowa", label: "w rodzinie zastępczej zawodowej, w tym pełniącej funkcję pogotowia rodzinnego albo specjalistycznej" },
            { value: "rdd", label: "w rodzinnym domu dziecka" },
          ],
        },
        {
          code: "drugaOsoba",
          type: "text",
          label: "Imię i nazwisko drugiej osoby tworzącej rodzinę zastępczą (małżonka), jeśli dotyczy",
          maxLength: 200,
        },
      ],
    },
    {
      code: "dziecko",
      title: "Dziecko",
      description: "Przy wniosku o środki na utrzymanie lokalu albo remont dane dziecka są nieobowiązkowe.",
      fields: [
        {
          code: "dzieckoImieNazwisko",
          type: "text",
          label: "Imię i nazwisko dziecka (pełnoletniego wychowanka)",
          requiredWhen: CHILD_REQUIRED,
          width: "half",
          maxLength: 200,
        },
        { code: "dzieckoDataUrodzenia", type: "date", label: "Data urodzenia", requiredWhen: CHILD_REQUIRED, width: "half" },
        {
          code: "dataUmieszczenia",
          type: "date",
          label: "Data faktycznego umieszczenia dziecka w rodzinie zastępczej lub rodzinnym domu dziecka",
          required: true,
          visibleWhen: MONTHLY,
          width: "half",
          hint: "Świadczenie i dodatek przysługują od dnia faktycznego umieszczenia dziecka (art. 87 ust. 1 ustawy).",
        },
        {
          code: "pelnoletni",
          type: "checkbox",
          label: "Wniosek dotyczy pełnoletniego wychowanka, który nadal przebywa w rodzinie zastępczej i kontynuuje naukę (art. 82 ustawy)",
          visibleWhen: MONTHLY,
        },
      ],
    },
    {
      code: "fakultatywneUzasadnienie",
      title: "Świadczenie fakultatywne",
      visibleWhen: OPTIONAL,
      fields: [
        { code: "kwotaWnioskowana", type: "number", label: "Wnioskowana kwota [zł]", required: true, width: "third", decimals: 2, min: 0 },
        { code: "uzasadnienie", type: "textarea", label: "Uzasadnienie — na co zostaną przeznaczone środki", required: true, maxLength: 3000 },
      ],
    },
    {
      code: "wyplata",
      title: "Sposób wypłaty",
      fields: [
        {
          code: "rachunek",
          type: "text",
          label: "Numer rachunku bankowego, na który ma być przekazywane świadczenie",
          maxLength: 40,
          placeholder: "00 0000 0000 0000 0000 0000 0000",
          hint: "Nieobowiązkowo — bez numeru rachunku PCPR ustali z Tobą sposób wypłaty.",
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "postanowienieSadu",
      label: "Postanowienie sądu o umieszczeniu dziecka w rodzinie zastępczej lub rodzinnym domu dziecka (albo umowa, o której mowa w art. 35 ust. 2 ustawy)",
      hint: "Skan prawomocnego postanowienia.",
      requiredWhen: MONTHLY,
    },
    {
      code: "aktUrodzenia",
      label: "Odpis skrócony aktu urodzenia dziecka",
      hint: "Praktyka PCPR — potwierdź z jednostką, czy jest wymagany.",
      visibleWhen: MONTHLY,
    },
    {
      code: "orzeczenieDziecka",
      label: "Orzeczenie o niepełnosprawności albo o znacznym lub umiarkowanym stopniu niepełnosprawności dziecka",
      visibleWhen: { all: [ALLOWANCE, { field: "podstawaDodatku", equals: "niepelnosprawnosc" }] },
      required: true,
    },
    {
      code: "zaswiadczenieNauka",
      label: "Zaświadczenie o kontynuowaniu nauki przez pełnoletniego wychowanka",
      visibleWhen: ADULT_PUPIL,
      required: true,
    },
    {
      code: "dokumentyWydatku",
      label: "Dokumenty uzasadniające wydatek",
      hint: "Np. oferta, umowa lub faktura za wypoczynek, kosztorys lub rachunki kosztów przyjęcia dziecka, dokumentacja zdarzenia losowego, rachunki za czynsz i media, kosztorys remontu (praktyka PCPR).",
      visibleWhen: OPTIONAL,
      multiple: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default H2;
